import { $t } from '@previo/core'
import type { UserId } from '@previo/codebooks'
import { toast, usePlatform } from '@previo/design-system'
import type { IPushNotificationClient } from '~/plugins/push-notifications/client/types/IPushNotificationClient'
import { useUserNotificationsAPI } from '~/plugins/push-notifications/client/http/UserNotificationAPI'
import type { TPushSubscriptionPayload } from '~/plugins/push-notifications/client/types/TPushSubscriptionPayload'
import type { TPushSubscriptionKeys } from '~/plugins/push-notifications/client/types/TPushSubscriptionKeys'

export class WebPushClient implements IPushNotificationClient {
  async start(userId: UserId): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications are not supported in this browser')
    }

    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      throw new Error('User denied push notifications')
    }

    const registration = await navigator.serviceWorker.register('/sw.js')

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(
        import.meta.env.PMS_FE_WEB_PUSH_VAPID_PUBLIC_KEY,
      ) as BufferSource,
    })

    await useUserNotificationsAPI().subscribe(this.buildSubscriptionPayload(subscription, userId))

    toast.positive($t('Push notifications have been enabled for this device.'))

    return Promise.resolve()
  }

  async stop(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const registration = await navigator.serviceWorker.getRegistration()

    if (!registration) {
      return
    }

    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      return
    }

    await useUserNotificationsAPI().unsubscribe(this.buildKeys(subscription))

    await subscription.unsubscribe()

    toast.positive($t('Push notifications have been disabled for this device.'))
  }

  canAutoRequestPermission(): boolean {
    return false
  }

  async deactivate(): Promise<void> {
    const subscription = await this.getSubscription()

    if (!subscription) {
      return Promise.resolve()
    }

    await useUserNotificationsAPI().deactivate(this.buildKeys(subscription))
  }

  async activate(): Promise<void> {
    const subscription = await this.getSubscription()

    if (!subscription) {
      return Promise.resolve()
    }

    await useUserNotificationsAPI().activate(this.buildKeys(subscription))
  }

  async isDeniedByBrowser(): Promise<boolean> {
    return Notification.permission === 'denied'
  }

  async isSubscribed(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()

      if (!registration) {
        return false
      }

      const subscription = await registration.pushManager.getSubscription()

      return !!subscription
    } catch {
      return false
    }
  }

  private buildSubscriptionPayload(
    subscription: PushSubscription,
    userId: UserId,
  ): TPushSubscriptionPayload {
    return {
      endpoint: subscription.endpoint,
      deviceType: usePlatform().getPlatformName(),
      userId: userId,
      ...this.buildKeys(subscription),
    }
  }

  private buildKeys(subscription: PushSubscription): TPushSubscriptionKeys {
    return {
      key: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
      auth: this.arrayBufferToBase64(subscription.getKey('auth')!),
    }
  }

  private async getSubscription(): Promise<PushSubscription | null> {
    if (!('serviceWorker' in navigator)) {
      return null
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()

      if (!registration) {
        return null
      }

      return await registration.pushManager.getSubscription()
    } catch {
      return null
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)

    let binary = ''

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }

    return btoa(binary)
  }
}
